import capitalizedName from '$utils/capitalizedName';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CellProps, Column, useTable } from 'react-table';
import RemoveMember from '../Members/RemoveMember';

const CapitalizedNameCell: FC<{ value: string }> = ({ value }) => (
  <span>{capitalizedName(value)}</span>
);

type ClientRow = {
  employerId: number;
  name: string;
  email: string;
  startDate: string;
  agreedTermsOfServiceDate: string | null;
};

const ClientsTable = ({
  companyId,
  data = [],
}: {
  companyId: number;
  data: ClientRow[];
}) => {
  const [t] = useTranslation('global');
  const columns = useMemo(
    (): Column<ClientRow>[] => [
      {
        Header: `${t('CompaniesTable.ClientName')}`,
        accessor: 'name',
        Cell: CapitalizedNameCell,
      },
      {
        Header: `${t('CompaniesTable.ClientMail')}`,
        accessor: 'email',
      },
      {
        Header: `${t('CompaniesTable.CreatedDate')}`,
        accessor: 'startDate',
      },
      {
        Header: `${t('CompaniesTable.AgreeTOSDate')}`,
        accessor: 'agreedTermsOfServiceDate',
      },
      {
        Header: `${t('CompaniesTable.Actions')}`,
        id: 'actions',
        Cell: ({ row }: CellProps<ClientRow>) => (
          <RemoveMember
            companyId={companyId}
            employerId={row.original.employerId}
            label={`${row.original.name} (${row.original.email})`}
          />
        ),
      },
    ],
    [t, companyId]
  );

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } =
    useTable({
      columns,
      data,
    });

  return (
    <table {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => (
              <th
                style={{
                  textAlign: 'center',
                }}
                {...column.getHeaderProps()}
              >
                {column.render('Header')}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row);
          return (
            <tr {...row.getRowProps()}>
              {row.cells.map((cell) => {
                return (
                  <td
                    style={{
                      textAlign: 'center',
                    }}
                    {...cell.getCellProps()}
                  >
                    {cell.render('Cell')}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default ClientsTable;
